/**
 * Results for a search across the archive: the partner units the term matches, then a
 * full-text search of newsletter bodies and prayer requests.
 * Backed by the FTS5 index, with a LIKE fallback if the index is unavailable.
 *
 * This was a page of its own. The box that drives it sits at the top of the
 * dashboard now, and the dashboard shows this in its own place once you type, so
 * what is left here is the query and the results — the input lives with the page.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi, qs } from '../lib/api'
import {
  Card, Badge, Empty, Loading, ErrorState, Pager, fmtInt, AlumniSwitch,
  ActivityBadge, CountryLabel, relDays,
} from './ui'
import { Icon } from './Icon'
import { useCode } from '../lib/terms'

/**
 * FTS5 `snippet()` wraps hits in <mark>. The surrounding text is tag-stripped on
 * import, but escaping everything and then restoring only the mark tags means a
 * stray unclosed angle bracket in the source can never become markup.
 */
function highlight(snippet) {
  const esc = String(snippet || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return esc.replace(/&lt;mark&gt;/g, '<mark>').replace(/&lt;\/mark&gt;/g, '</mark>')
}

export default function SearchResults({ q }) {
  const code = useCode()
  const [page, setPage] = useState(1)
  // A hit on an alumni update would open a record the Updates list will not show, so
  // search keeps the same default as everywhere else.
  const [withAlumni, setWithAlumni] = useState(false)

  // Page three of the last search is not page three of this one, so a new query —
  // or alumni coming in or out of one — starts again at the first page. Adjusted
  // while rendering rather than in an effect afterwards: by the time an effect ran,
  // a request for page three of a list that may only have one had already gone out.
  const [pagedFor, setPagedFor] = useState(`${q}|${withAlumni}`)
  if (pagedFor !== `${q}|${withAlumni}`) {
    setPagedFor(`${q}|${withAlumni}`)
    setPage(1)
  }

  const query = useApi(
    ['search', q, page, withAlumni],
    `search${qs({ q, page, limit: 25, include_alumni: withAlumni ? 1 : null })}`,
    { enabled: q.length > 1, keepPreviousData: true }
  )

  return (
    <div className="col">
      {/* The count and the alumni toggle share one line above the results: the
          toggle changes what is counted, so it belongs beside the number it
          moves rather than up in the page header. */}
      <div className="row small muted">
        {/* Says what it counted. It used to read "0 matches" over a card that had plainly
            found something: the number has always been the updates alone, and now that a
            unit can match too, "matches" was the wrong word for it. */}
        {query.data && (
          <span>
            {query.data.partners?.length
              ? `${fmtInt(query.data.partners.length)} partner unit${
                  query.data.partners.length === 1 ? '' : 's'
                } and `
              : ''}
            {fmtInt(query.data.total)} update{query.data.total === 1 ? '' : 's'} match
            {query.data.total === 1 && !query.data.partners?.length ? 'es' : ''} “{q}”
          </span>
        )}
        {query.data?.mode === 'like' && (
          <Badge tone="warn" title="The full-text index was unavailable; fell back to a slower substring scan">
            substring mode
          </Badge>
        )}
        <span className="spacer" />
        <AlumniSwitch checked={withAlumni} onChange={setWithAlumni} where="updates" />
      </div>

      {query.isLoading && (
        <Card>
          <Loading label="Searching…" />
        </Card>
      )}
      {query.isError && (
        <Card>
          <ErrorState error={query.error} retry={query.refetch} />
        </Card>
      )}

      {/*
        * The partner units the term matches, above the updates.
        *
        * This box is the only search on the page, and searching an archive for a unit is a
        * different question from searching it for a phrase. It is also the only way in to
        * the two names the archive holds and never shows — an alias and a preferred name,
        * searchable so that somebody who knows one can find the record, and displayed
        * nowhere. What is drawn here is what is drawn everywhere else: the code, the
        * initials, the country. Never the name that matched.
        *
        * Alumni units are here whether or not the switch is on, unlike the updates below.
        * Somebody typing a name they know is looking for that record, and answering
        * "nothing found" because the unit retired would be the archive pretending not to
        * have it. The status badge says which are which.
        */}
      {query.data?.partners?.length ? (
        <Card
          title={`Partner Unit${query.data.partners.length === 1 ? '' : 's'}`}
          sub="Matched on the code, the name, or a name held for searching"
        >
          <div className="col">
            {query.data.partners.map((p) => (
              <div className="search-unit" key={p.partner_id}>
                <Link className="mono small" to={`/partners/${p.partner_id}`}>
                  {code(p.s4ca_code)}
                </Link>
                <Link className="search-unit-name truncate" to={`/partners/${p.partner_id}`}>
                  {p.display_name}
                </Link>
                <span className="small truncate">
                  <CountryLabel id={p.country_id} name={p.country_name} flag={p.flag_emoji} />
                </span>
                <span className="spacer" />
                <span className="tiny subtle nowrap">
                  <Icon name="mail" size={12} /> {fmtInt(p.newsletter_count)}
                  {p.last_newsletter ? ` · ${relDays(p.last_newsletter)}` : ''}
                </span>
                <ActivityBadge activity={p.activity} />
                {p.status !== 'active' ? <Badge>{p.status}</Badge> : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {query.data &&
        (!query.data.rows.length ? (
          !query.data.partners?.length ? (
            <Card>
              <Empty title="Nothing found">Try fewer or more general words.</Empty>
            </Card>
          ) : null
        ) : (
          <div className="col">
            {query.data.rows.map((r) => (
              <Card key={r.id}>
                <div className="row row-tight small" style={{ marginBottom: 6 }}>
                  <Link to={`/newsletters/${r.id}`} className="mono">
                    {r.published_on}
                  </Link>
                  <span className="subtle">·</span>
                  <span className="muted">{code(r.s4ca_code)}</span>
                  <span>{r.partner_name}</span>
                  <span className="subtle">·</span>
                  <span className="subtle">{r.country_name}</span>
                  {r.highlighted_need ? <Badge tone="warn"><Icon name="star" size={12} /></Badge> : null}
                </div>
                {r.body_snippet && (
                  <div
                    className="prose small"
                    dangerouslySetInnerHTML={{ __html: highlight(r.body_snippet) }}
                  />
                )}
                {r.prayer_snippet && (
                  <div className="row row-tight" style={{ marginTop: 8, color: 'var(--accent)', alignItems: 'flex-start' }}>
                    <Icon name="prayer" size={14} className="icon search-prayer-icon" />
                    <div
                      className="prose small"
                      dangerouslySetInnerHTML={{ __html: highlight(r.prayer_snippet) }}
                    />
                  </div>
                )}
              </Card>
            ))}
            <div className="card">
              <Pager
                page={query.data.page}
                pages={Math.ceil(query.data.total / query.data.limit)}
                total={query.data.total}
                limit={query.data.limit}
                onPage={setPage}
              />
            </div>
          </div>
        ))}
    </div>
  )
}
